'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();

import {
  ComponentProps,
  createContext,
  ElementType,
  ReactNode,
  useContext,
  useState,
} from 'react'

import { cn } from '../../utils/cn'
import { Icon } from './icon'

const NavigationDepthContext = createContext(0)
const IsCollapsedContext = createContext(false)
const SetIsCollapsedContext = createContext<null | React.Dispatch<
  React.SetStateAction<boolean>
>>(null)
const IsActiveContext = createContext<boolean | null>(null)

/**
 * @returns Returns the current depth of the navigation tree
 */
function useNavigationDepth() {
  return useContext(NavigationDepthContext)
}

function useIsCollapsedContext() {
  return useContext(IsCollapsedContext)
}

function useSetIsCollapsedContext() {
  const collapse = useContext(SetIsCollapsedContext)
  if (!collapse) {
    throw new Error('SetIsCollapsedContext is not defined')
  }
  return collapse
}

function useIsSectionActiveContext() {
  const isSectionActive = useContext(IsActiveContext)
  if (isSectionActive === null) {
    throw new Error('IsActiveContext is not defined')
  }
  return isSectionActive
}

function useIsActiveContext() {
  const isActive = useContext(IsActiveContext)
  return Boolean(isActive)
}

export function Navigation({ className, ...rest }: ComponentProps<'ul'>) {
  return <ul className={cn('flex flex-col', className)} {...rest} />
}

export function NavigationAdornment({
  isActive,
  className,
  ...rest
}: ComponentProps<'div'> & { isActive: boolean }) {
  return (
    <div
      className={cn(
        'flex grow before:absolute before:top-1 before:bottom-1 before:w-1 before:rounded-r',
        { 'before:bg-gray-800': isActive },
        className,
      )}
      {...rest}
    />
  )
}

export function NavigationIcon({ children }: { children: ReactNode }) {
  const isActive = useIsActiveContext()
  return (
    <div
      className={cn('mr-3 w-4.5 shrink-0', {
        'text-gray-400': !isActive,
        'text-primary-700': isActive,
      })}
      aria-hidden="true"
    >
      {children}
    </div>
  )
}

export function NavigationInsetMargin({ insetLevel }: { insetLevel: number }) {
  return (
    <>
      {Array.from({ length: insetLevel }, (_, index) => (
        <span key={index} className="w-7.5" />
      ))}
    </>
  )
}

type NavigationListItemProps<C extends ElementType> = {
  as?: C
  children?: ReactNode
  isActive: boolean
} & Omit<ComponentProps<C>, 'as'>

/**
 * Internal <li> used by single items and section headings.
 * (Not exposed in public API)
 */
function NavigationListItem<C extends ElementType>({
  as,
  children,
  isActive,
  className,
  ...componentProps
}: NavigationListItemProps<C>) {
  const Component = as || 'button'
  const type = Component === 'button' ? 'button' : undefined
  const ariaCurrent = 'href' in componentProps && isActive ? 'page' : undefined

  return (
    <li className="group relative flex py-1">
      <NavigationAdornment isActive={isActive}>
        <Component
          {...componentProps}
          className={cn(
            'mr-1.5 ml-3 flex grow flex-row flex-nowrap items-center rounded-sm py-3 pr-2 pl-3 text-left outline-hidden group-hover:bg-gray-200 focus-visible:bg-gray-200',
            {
              'text-primary-700 bg-gray-100': isActive,
              'text-gray-900': !isActive,
            },
            className,
          )}
          type={type}
          aria-current={ariaCurrent}
        >
          {children}
        </Component>
      </NavigationAdornment>
    </li>
  )
}

export function NavigationItem<C extends ElementType>({
  children,
  isActive,
  ...componentProps
}: NavigationListItemProps<C>) {
  const navigationDepth = useNavigationDepth()

  return (
    <IsActiveContext.Provider value={isActive}>
      {/* @ts-expect-error TODO: fix types */}
      <NavigationListItem {...componentProps} isActive={isActive}>
        {/*
          This fragment is important to prevent Playwright Component tests
          from incorrectly breaking during build when Link components are used with multiple children
         */}
        <>
          <NavigationInsetMargin insetLevel={navigationDepth} />
          {children}
        </>
      </NavigationListItem>
    </IsActiveContext.Provider>
  )
}

export function NavigationSectionHeading({
  children,
  className,
  ...componentProps
}: Omit<
  NavigationListItemProps<'button'>,
  'as' | 'onClick' | 'type' | 'isActive'
>) {
  const navigationDepth = useNavigationDepth()
  const isCollapsed = useIsCollapsedContext()
  const setIsCollapsed = useSetIsCollapsedContext()
  const isSectionActive = useIsSectionActiveContext()

  const isActive = isCollapsed && isSectionActive
  const onClick = () => setIsCollapsed((previous) => !previous)
  const arrowIconName = isCollapsed
    ? 'heroicons/solid/chevron-right'
    : 'heroicons/solid/chevron-down'

  return (
    <NavigationListItem
      {...componentProps}
      isActive={isActive}
      as="button"
      onClick={onClick}
      aria-expanded={!isCollapsed}
      className={cn('font-light', className)}
    >
      <NavigationInsetMargin insetLevel={navigationDepth - 1} />
      {children}
      <div className="ml-auto">
        <Icon
          name={arrowIconName}
          className="ml-3 h-4 w-4 shrink-0 text-gray-400"
          aria-hidden="true"
        />
      </div>
    </NavigationListItem>
  )
}

export function NavigationSection({
  children,
  isActive,
}: {
  children: ReactNode
  isActive: boolean
}) {
  const [isCollapsed, setIsCollapsed] = useState(!isActive)

  const navigationDepth = useNavigationDepth()

  return (
    <NavigationDepthContext.Provider value={navigationDepth + 1}>
      <IsCollapsedContext.Provider value={isCollapsed}>
        <IsActiveContext.Provider value={isActive}>
          <SetIsCollapsedContext.Provider value={setIsCollapsed}>
            {children}
          </SetIsCollapsedContext.Provider>
        </IsActiveContext.Provider>
      </IsCollapsedContext.Provider>
    </NavigationDepthContext.Provider>
  )
}
export function NavigationSectionItems({
  className,
  ...rest
}: ComponentProps<'ul'>) {
  const isCollapsed = useIsCollapsedContext()

  return (
    <ul
      className={cn(
        'flex flex-col',
        {
          hidden: isCollapsed,
        },
        className,
      )}
      {...rest}
    />
  )
}
